import { differenceInCalendarDays } from 'date-fns'
import {
  type CalendarDensity,
  type CalendarSortBy,
} from './MonthCalendarGrid/types'
import {
  type CalendarWeek,
  DAYS_IN_WEEK,
  makeDayKey,
  parseCalendarDate,
} from './monthGrid'
import { cardBlockSize, CalendarSizes } from './sizes'
import { type CalendarItem, type CalendarItemKind } from './types'

/** A moment, and the run of days it covers. */
export type CalendarBar = {
  end: Date
  item: CalendarItem
  key: string
  start: Date
}

/** A bar clipped to one week row, before it is given a lane in that row. */
export type WeekBar = CalendarBar & {
  /** True when the item really ends inside this week, not just clipped to it. */
  isEnd: boolean
  /** True when the item really starts inside this week. */
  isStart: boolean
  /** 1-based CSS grid column of the first visible day. */
  startColumn: number
  /** Day columns covered inside this week. */
  span: number
}

export type PlacedBar = WeekBar & {
  /** Row within the bar stack, 0 at the top. */
  lane: number
}

export type WeekLanes = {
  bars: PlacedBar[]
  /**
   * Bars the cap left no room for. They are not drawn, so every day they cover
   * counts them behind its "+N more" instead.
   */
  hiddenBars: WeekBar[]
  laneCount: number
}

const readRange = (
  item: CalendarItem,
  start: string | undefined,
  end: string | undefined,
  field: string
): [Date, Date] | null => {
  const startDate = parseCalendarDate(start, {
    field: `${field}.start`,
    id: item.id,
  })
  const endDate = parseCalendarDate(end, { field: `${field}.end`, id: item.id })
  if (startDate === null || endDate === null) return null

  if (endDate < startDate) {
    console.error('Calendar item ends before it starts, skipping', {
      end,
      field,
      itemId: item.id,
      start,
    })
    return null
  }

  return [startDate, endDate]
}

export type BuildCalendarBarsOptions = {
  /** @default ['moment'] */
  kinds?: readonly CalendarItemKind[]
}

/**
 * Turns the items a surface draws as bars into the bars the week rows draw.
 *
 * A bar is drawn even for a single day — the kinds that get one are containers,
 * and the bar is what tells you which days they cover. The month draws moments
 * this way and content in the chip column; a surface that stands blocks over
 * their content says so with `kinds`.
 */
export const buildCalendarBars = (
  items: readonly CalendarItem[],
  { kinds = ['moment'] }: BuildCalendarBarsOptions = {}
): CalendarBar[] => {
  const bars: CalendarBar[] = []

  items.forEach((item) => {
    if (!kinds.includes(item.kind)) return

    const range = readRange(item, item.start, item.end, 'schedule')
    if (range === null) return

    bars.push({
      end: range[1],
      item,
      key: `${item.kind}-${item.id}`,
      start: range[0],
    })
  })

  return bars
}

/** How many days an item covers over its whole schedule, not over one week. */
const coveredDays = (bar: { end: Date; start: Date }): number =>
  differenceInCalendarDays(bar.end, bar.start) + 1

/**
 * Which moment a week row keeps when it cannot keep them all: the longest,
 * because the moment that runs through a week says the most about it. Ties go
 * to the earlier start, then the title, then the key, so a re-fetch that
 * returns the items in a different order cannot change what the week draws.
 */
const compareByLength = (a: CalendarBar, b: CalendarBar): number =>
  coveredDays(b) - coveredDays(a) ||
  a.start.getTime() - b.start.getTime() ||
  a.item.title.localeCompare(b.item.title) ||
  a.key.localeCompare(b.key)

/**
 * The order the lanes are handed out in: earliest day first, so a lane can be
 * given again to anything that begins after the last bar in it has ended. Ties
 * go to the title, then the key, so a re-fetch cannot reshuffle a week.
 */
const compareByStart = (a: WeekBar, b: WeekBar): number =>
  a.startColumn - b.startColumn ||
  a.item.title.localeCompare(b.item.title) ||
  a.key.localeCompare(b.key)

export type PlaceBarsInWeekOptions = {
  /**
   * Most lanes this week row can draw. Absent means it can hold them all,
   * which is what a row that grows to fit does.
   */
  laneCap?: number
}

/** The 1-based columns of a week row a bar reaches over. */
const columnsOf = ({ span, startColumn }: WeekBar): number[] =>
  Array.from({ length: span }, (_unused, index) => startColumn + index)

/**
 * Places the bars that touch one week row, clipping each to the row.
 *
 * Which bars are drawn and where they sit are two different questions. Length
 * answers the first: a capped row keeps the longest moments, and a moment is
 * kept while every day it covers is still shallower than the cap, because a
 * day needs one lane for each moment over it and no more.
 *
 * The day a bar starts answers the second. The bars that survive take the
 * lowest lane whose last day is behind them, in the order they begin, which is
 * the fewest lanes the row can be drawn in — so a moment that ends on Tuesday
 * and one that starts on Wednesday share the top lane rather than stacking.
 */
export const placeBarsInWeek = (
  bars: readonly CalendarBar[],
  week: CalendarWeek,
  { laneCap }: PlaceBarsInWeekOptions = {}
): WeekLanes => {
  const weekStart = week.days[0].date
  const weekEnd = week.days[DAYS_IN_WEEK - 1].date

  const inWeek = bars
    .filter((bar) => bar.start <= weekEnd && bar.end >= weekStart)
    .map((bar) => {
      const startColumn =
        Math.max(0, differenceInCalendarDays(bar.start, weekStart)) + 1
      const endColumn =
        Math.min(
          DAYS_IN_WEEK - 1,
          differenceInCalendarDays(bar.end, weekStart)
        ) + 1

      return {
        ...bar,
        isEnd: bar.end <= weekEnd,
        isStart: bar.start >= weekStart,
        span: endColumn - startColumn + 1,
        startColumn,
      }
    })

  // How many bars already cover each day of the row, which is how many lanes
  // that day needs.
  const depth = new Array<number>(DAYS_IN_WEEK + 2).fill(0)
  const drawn: WeekBar[] = []
  const hiddenBars: WeekBar[] = []

  ;[...inWeek].sort(compareByLength).forEach((bar) => {
    const columns = columnsOf(bar)
    if (
      laneCap !== undefined &&
      columns.some((column) => depth[column] >= laneCap)
    ) {
      hiddenBars.push(bar)
      return
    }

    columns.forEach((column) => {
      depth[column] += 1
    })
    drawn.push(bar)
  })

  // Last column occupied by each lane, so a bar can reuse a lane it clears.
  const laneEnds: number[] = []
  const placed = drawn.sort(compareByStart).map((bar) => {
    let lane = laneEnds.findIndex((lastColumn) => lastColumn < bar.startColumn)
    if (lane === -1) lane = laneEnds.length
    laneEnds[lane] = bar.startColumn + bar.span - 1

    return { ...bar, lane }
  })

  return { bars: placed, hiddenBars, laneCount: laneEnds.length }
}

/** A piece of content, and the run of days it is live on its channel. */
export type CalendarSpan = {
  end: Date
  item: CalendarItem
  key: string
  start: Date
}

export type PlacedSpan = CalendarSpan & {
  /** True when the window really ends inside this week. */
  isEnd: boolean
  /** True when the window really starts inside this week. */
  isStart: boolean
  /** Row of the day's chip column the span is drawn in, 0 at the top. */
  row: number
  /** 1-based CSS grid column of the first visible day. */
  startColumn: number
  /** Day columns covered inside this week. */
  span: number
}

export type WeekSpans = {
  /**
   * The rows each day has given up to a span, keyed `yyyy-MM-dd`. A day that
   * only passes under a span still gives up the row, so the span stays a
   * straight line across the week.
   */
  reservedRowsByDay: Map<string, number[]>
  spans: PlacedSpan[]
}

export type BuildContentSpansOptions = {
  /** @default ['content'] */
  kinds?: readonly CalendarItemKind[]
  /** @default true */
  shouldShowInMarketBars?: boolean
}

/**
 * Turns the content that is live for more than a day into the spans the chip
 * column draws across those days.
 *
 * A window of a single day is left alone: the content's own chip already sits
 * on that day, so a span would say the same thing twice.
 */
export const buildContentSpans = (
  items: readonly CalendarItem[],
  {
    kinds = ['content'],
    shouldShowInMarketBars = true,
  }: BuildContentSpansOptions
): CalendarSpan[] => {
  if (!shouldShowInMarketBars) return []

  const spans: CalendarSpan[] = []

  items.forEach((item) => {
    if (!kinds.includes(item.kind) || !item.inMarket) return

    const range = readRange(
      item,
      item.inMarket.start,
      item.inMarket.end,
      'inMarket'
    )
    if (range === null) return
    if (differenceInCalendarDays(range[1], range[0]) < 1) return

    spans.push({
      end: range[1],
      item,
      key: `in-market-${item.id}`,
      start: range[0],
    })
  })

  return spans
}

/**
 * Spans pack earliest first, then longest, then by key. The rows are worked
 * out one week at a time, so a span can sit at a different row in the week
 * after this one; what the order buys is that a re-fetch which returns the
 * items differently cannot reshuffle a day.
 */
const compareSpans = (a: CalendarSpan, b: CalendarSpan): number =>
  a.start.getTime() - b.start.getTime() ||
  b.end.getTime() - a.end.getTime() ||
  a.key.localeCompare(b.key)

/**
 * Places the spans that touch one week row, clipping each to the row and
 * giving it the first row of the chip column with space across every day it
 * covers.
 */
export const placeSpansInWeek = (
  spans: readonly CalendarSpan[],
  week: CalendarWeek
): WeekSpans => {
  const weekStart = week.days[0].date
  const weekEnd = week.days[DAYS_IN_WEEK - 1].date
  // Last column occupied by each row, so a span can reuse a row it clears.
  const rowEnds: number[] = []
  const placed: PlacedSpan[] = []
  const reservedRowsByDay = new Map<string, number[]>()

  spans
    .filter((span) => span.start <= weekEnd && span.end >= weekStart)
    .sort(compareSpans)
    .forEach((span) => {
      const startColumn =
        Math.max(0, differenceInCalendarDays(span.start, weekStart)) + 1
      const endColumn =
        Math.min(
          DAYS_IN_WEEK - 1,
          differenceInCalendarDays(span.end, weekStart)
        ) + 1

      let row = rowEnds.findIndex((lastColumn) => lastColumn < startColumn)
      if (row === -1) row = rowEnds.length
      rowEnds[row] = endColumn

      placed.push({
        ...span,
        isEnd: span.end <= weekEnd,
        isStart: span.start >= weekStart,
        row,
        span: endColumn - startColumn + 1,
        startColumn,
      })

      for (let column = startColumn; column <= endColumn; column += 1) {
        const dayKey = week.days[column - 1].key
        const rows = reservedRowsByDay.get(dayKey)
        if (rows) rows.push(row)
        else reservedRowsByDay.set(dayKey, [row])
      }
    })

  return { reservedRowsByDay, spans: placed }
}

/**
 * One day's chip column, row by row, in the order it is drawn.
 *
 * `null` is a row the day has no chip for: either a span is drawn over it, or
 * it is the gap a day keeps under a span that starts further down the column.
 * The gap is the price of a span reading as one straight line across the week.
 */
export const buildDayRows = <T>(
  reservedRows: readonly number[],
  items: readonly T[]
): (T | null)[] => {
  const reserved = new Set(reservedRows)
  const lastReserved = reservedRows.reduce(
    (last, row) => Math.max(last, row),
    -1
  )
  const rows: (T | null)[] = []
  let next = 0

  while (next < items.length || rows.length <= lastReserved) {
    if (reserved.has(rows.length)) rows.push(null)
    else {
      rows.push(items[next] ?? null)
      next += 1
    }
  }

  return rows
}

/** The order content reads in on a single day, wherever that day is drawn. */
export const compareChips = (a: CalendarItem, b: CalendarItem): number => {
  // Content with no time chosen sorts after content that has one, matching how
  // `start_interval === null` reads everywhere else in the product.
  if (!a.startsAt !== !b.startsAt) return a.startsAt ? -1 : 1
  return (
    (a.startsAt ?? '').localeCompare(b.startsAt ?? '') ||
    a.title.localeCompare(b.title)
  )
}

export type BuildChipsByDayOptions = {
  /**
   * Items a span already draws. A piece of content drawn across its in-market
   * days is not given a chip on its launch day as well: the span covers that
   * day, so the two would say the same thing twice.
   */
  idsDrawnAsSpans?: ReadonlySet<string>
  /** @default ['content'] */
  kinds?: readonly CalendarItemKind[]
}

/** Indexes the chips each day shows, keyed `yyyy-MM-dd` and sorted for display. */
export const buildChipsByDay = (
  items: readonly CalendarItem[],
  { idsDrawnAsSpans, kinds = ['content'] }: BuildChipsByDayOptions = {}
): Map<string, CalendarItem[]> => {
  const chipsByDay = new Map<string, CalendarItem[]>()

  items.forEach((item) => {
    if (!kinds.includes(item.kind)) return
    if (idsDrawnAsSpans?.has(item.id)) return
    const date = parseCalendarDate(item.start, { field: 'start', id: item.id })
    if (date === null) return

    const key = makeDayKey(date)
    const day = chipsByDay.get(key)
    if (day) day.push(item)
    else chipsByDay.set(key, [item])
  })

  chipsByDay.forEach((day) => day.sort(compareChips))

  return chipsByDay
}

export const sortValues: Readonly<
  Record<
    Exclude<CalendarSortBy, 'none'>,
    (item: CalendarItem) => string | undefined
  >
> = {
  account: (item) => item.accountId,
  channel: (item) => item.channelId,
  // The first label is the primary one: the same label whose colour a chip
  // wears, so sorting and colouring never disagree about which label counts.
  label: (item) => item.labelIds?.[0],
  moment: (item) => item.parentId,
}

/** One day's chips in the order they are drawn, and where the runs begin. */
export type DayChipOrder = Readonly<{
  chips: readonly CalendarItem[]
  /**
   * The chips that open a run after the first one. They are drawn a `runGap`
   * lower, which is the only thing that says where one run ends and the next
   * begins.
   */
  runStartIds: ReadonlySet<string>
}>

export type BuildDayChipOrderOptions = {
  /** The moment a piece of content belongs to, for sorting by moment. */
  parentOf?: (item: CalendarItem) => CalendarItem | undefined
}

/**
 * Puts one day's chips in runs of the same value, biggest run first.
 *
 * Runs of the same size are ordered by their key so a re-fetch that returns
 * items in a different order cannot reshuffle a day, and the run for items with
 * no value of their own goes last. Inside a run the day keeps the order it came
 * in, which is the time order every day is read in.
 *
 * A run of moments is ordered by the day its moment starts instead: the runs
 * then read down the day the way the moment bars read across the week.
 */
export const buildDayChipOrder = (
  chips: readonly CalendarItem[],
  sortBy: CalendarSortBy,
  { parentOf }: BuildDayChipOrderOptions = {}
): DayChipOrder => {
  if (sortBy === 'none') return { chips, runStartIds: new Set() }

  const valueOf = sortValues[sortBy]
  const byKey = new Map<string, CalendarItem[]>()

  chips.forEach((item) => {
    const key = valueOf(item) ?? ''
    const run = byKey.get(key)
    if (run) run.push(item)
    else byKey.set(key, [item])
  })

  const parentOfRun = (items: readonly CalendarItem[]) => parentOf?.(items[0])

  // How far down the day a run goes before anything else is weighed. Sorting
  // by moment only reads while the runs go down the day the way the bars go
  // across the week, and a moment the calendar cannot see has no place in that
  // order — so it sinks, with the content that belongs to no moment last of
  // all.
  const rank = ([key, items]: [string, readonly CalendarItem[]]): number => {
    if (key === '') return 2
    return sortBy === 'moment' && parentOfRun(items) === undefined ? 1 : 0
  }

  const compareRuns = (
    a: [string, readonly CalendarItem[]],
    b: [string, readonly CalendarItem[]]
  ): number => {
    const sunk = rank(a) - rank(b)
    if (sunk !== 0) return sunk

    if (sortBy === 'moment') {
      const parentA = parentOfRun(a[1])
      const parentB = parentOfRun(b[1])
      return (
        (parentA?.start ?? '').localeCompare(parentB?.start ?? '') ||
        (parentA?.title ?? '').localeCompare(parentB?.title ?? '') ||
        a[0].localeCompare(b[0])
      )
    }

    return b[1].length - a[1].length || a[0].localeCompare(b[0])
  }

  const runs = [...byKey].sort(compareRuns)

  return {
    chips: runs.flatMap(([, items]) => items),
    runStartIds: new Set(
      runs.slice(1).map(([, items]) => items[0].id)
    ) as ReadonlySet<string>,
  }
}

/**
 * The rows of a day's column that open a run, and so are drawn a `runGap`
 * lower.
 *
 * A row above a span never opens one. A span is drawn at a fixed offset down
 * the column so that it reads as one straight line across the week, so nothing
 * over it is allowed to change height.
 */
export const runStartRows = (
  rows: readonly (CalendarItem | null)[],
  {
    reservedRows,
    runStartIds,
  }: { reservedRows: readonly number[]; runStartIds: ReadonlySet<string> }
): Set<number> => {
  const lastReserved = reservedRows.reduce(
    (last, row) => Math.max(last, row),
    -1
  )
  const starts = new Set<number>()

  rows.forEach((row, index) => {
    if (row !== null && index > lastReserved && runStartIds.has(row.id))
      starts.add(index)
  })

  return starts
}

export type ComputeDayBudgetParams = {
  /**
   * The day holds something the grid could not draw at all: a moment whose
   * lane the week row had no room for, or a span that another day it crosses
   * could not hold. Such a day owes a "+N more" row however well its own rows
   * fit, so the row has to come out of the height before anything is counted.
   */
  hasHiddenItems?: boolean
  /** Bar lanes above the chips in this week row. */
  laneCount: number
  rowBlockSize: number
  /**
   * Height of every row the day would draw, in the order it draws them,
   * including the gap under each one. Rows are not all the same height: a span
   * row, a chip and a group header are each measured on their own.
   */
  rowHeights: readonly number[]
}

export type DayBudget = {
  visibleCount: number
}

/**
 * Decides how many chips a day cell shows before the "+N more" row takes over.
 *
 * The height of a chip and of the chrome around it are the same constants the
 * stylesheet paints with, so nothing has to be measured in the DOM.
 */
const chromeBlockSize = (laneCount: number): number =>
  CalendarSizes.dayHeaderBlockSize +
  CalendarSizes.chipGap +
  CalendarSizes.cellPaddingBlockEnd +
  CalendarSizes.laneStackGap +
  laneCount * (CalendarSizes.barBlockSize + CalendarSizes.laneGap)

const chipRowBlockSize = CalendarSizes.chipBlockSize + CalendarSizes.chipGap
const overflowRowBlockSize =
  CalendarSizes.overflowBlockSize + CalendarSizes.chipGap

/**
 * How tall one row of a day's column is.
 *
 * A card shrinks with the moment badge turned off, so the day holds more of
 * them.
 */
export const dayRowBlockSize = ({
  density = 'row',
  shouldShowMomentBadge,
}: {
  density?: CalendarDensity
  shouldShowMomentBadge?: boolean
}): number => {
  if (density === 'row') return chipRowBlockSize
  return cardBlockSize({ shouldShowMomentBadge }) + CalendarSizes.chipGap
}

/**
 * How many bar lanes a week row of a fixed height can draw.
 *
 * The month is a content calendar first, so the lanes take at most a third of
 * a day's free height and the content keeps the rest. On top of that the row
 * still has to hold the chrome, one row of its own content and the "+N more"
 * row that answers for the rest — a bound that only bites once a card is
 * taller than a third of the room, which no card is today.
 *
 * A week always shows one lane, however little room it has: a moment nobody
 * can see says nothing at all about the week. The moments past the cap are
 * counted behind "+N more" like any other item a day could not fit.
 */
export const computeLaneCap = ({
  rowBlockSize,
  rowHeight = chipRowBlockSize,
}: {
  rowBlockSize: number
  rowHeight?: number
}): number => {
  const available = rowBlockSize - chromeBlockSize(0)
  const laneBlockSize = CalendarSizes.barBlockSize + CalendarSizes.laneGap

  return Math.max(
    1,
    Math.min(
      Math.floor(available / 3 / laneBlockSize),
      Math.floor((available - rowHeight - overflowRowBlockSize) / laneBlockSize)
    )
  )
}

/**
 * How tall a week row is when the month is to fill the room it has.
 *
 * A month of fixed rows is read as a shape, so the rows share the height of
 * the grid rather than each taking a constant and leaving a band of nothing
 * under the last week. Below the standard row there is no shape left to read,
 * so the rows stop shrinking and the month scrolls instead.
 *
 * The height is measured by the grid and passed in. Nothing here reads the
 * DOM: the budget stays a pure function of the numbers it is given.
 */
export const fillWeekRowBlockSize = ({
  availableBlockSize,
  weekCount,
}: {
  availableBlockSize: number
  weekCount: number
}): number =>
  Math.max(
    CalendarSizes.weekRowBlockSize,
    Math.floor(availableBlockSize / weekCount)
  )

/**
 * How tall a week row is once its bar lanes are accounted for.
 *
 * Only a month that scrolls asks: its rows grow rather than letting the bars
 * spill over the weeks below. A month of fixed rows caps the lanes instead —
 * see `computeLaneCap`. Whatever happens, a crowded day keeps room for one row
 * and its "+N more".
 */
export const computeWeekRowBlockSize = ({
  laneCount,
  rowBlockSize,
  rowHeight = chipRowBlockSize,
}: {
  laneCount: number
  rowBlockSize: number
  rowHeight?: number
}): number =>
  Math.max(
    rowBlockSize,
    chromeBlockSize(laneCount) + rowHeight + overflowRowBlockSize
  )

export const computeDayBudget = ({
  hasHiddenItems = false,
  laneCount,
  rowBlockSize,
  rowHeights,
}: ComputeDayBudgetParams): DayBudget => {
  const available = rowBlockSize - chromeBlockSize(laneCount)
  const fit = (room: number): number => {
    let used = 0
    let count = 0
    while (count < rowHeights.length && used + rowHeights[count] <= room) {
      used += rowHeights[count]
      count += 1
    }
    return count
  }

  if (!hasHiddenItems && fit(available) === rowHeights.length) {
    return { visibleCount: rowHeights.length }
  }

  // The "+N more" row takes its own height off the top, which is less than a
  // row of its own once cards are twice the height of the row it costs.
  return { visibleCount: fit(available - overflowRowBlockSize) }
}
